import { useEffect, useState } from 'react';
import { getAffaireFacadeImage } from '../api/expertise-detail.api';
import { UseFacadeImageReturn } from '../types/expertise-detail.types';

export const useFacadeImage = (affaireId: string | undefined): UseFacadeImageReturn => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!affaireId) {
      setLoading(false);
      setError('No affaire ID provided');
      return;
    }

    let isMounted = true;
    let blobUrl: string | null = null;

    const fetchImage = async () => {
      setLoading(true);
      setError(null);

      try {
        // First, try to get the facade image URL from API
        const response = await getAffaireFacadeImage(affaireId);

        if (!isMounted) return;

        if (response.success && response.data) {
          const { downloadUrl, url } = response.data;
          const finalUrl = downloadUrl || url;

          if (finalUrl) {
            // Fetch the image with authentication
            const token = localStorage.getItem('authToken');
            if (!token) {
              throw new Error('No authentication token available');
            }

            const imageResponse = await fetch(finalUrl, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });

            if (!imageResponse.ok) {
              throw new Error('Failed to fetch image');
            }

            // Create blob URL for the image
            const blob = await imageResponse.blob();
            blobUrl = URL.createObjectURL(blob);

            if (isMounted) {
              setImageUrl(blobUrl);
              setLoading(false);
            }
          } else {
            // No image available
            if (isMounted) {
              setImageUrl(null);
              setLoading(false);
            }
          }
        } else {
          // API call failed
          if (isMounted) {
            setError(response.message || 'Failed to fetch facade image');
            setLoading(false);
          }
        }
      } catch (err) {
        console.error('Error fetching facade image:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to fetch facade image');
          setLoading(false);
        }
      }
    };

    fetchImage();

    // Cleanup function to revoke blob URL
    return () => {
      isMounted = false;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [affaireId, retryCount]);

  const retry = () => {
    setRetryCount(prev => prev + 1);
  };

  return {
    imageUrl,
    loading,
    error,
    retry,
  };
};